import { ViewIds } from '@ardoq/api-types';
import { ViewLegendContainerElement } from '@ardoq/graph';
import {
  EXPORT_FORMAT,
  type ExportLabels,
  canvasToBlob,
  deliverExport,
  getCanvasWithBackgroundColor,
} from '@ardoq/export';
import { colors } from '@ardoq/design-tokens';
import { getStyleSheets } from 'tabview/getSharedExportFunctions';
import { runViewExport } from 'tabview/runViewExport';

type ExportCanvas = HTMLCanvasElement | OffscreenCanvas | null;

type PngExporterArgs = {
  getExportContainer: () => HTMLElement | null;
  /** A view's canvas, or a promise for one it has to render first. */
  getExportCanvas: () => ExportCanvas | Promise<ExportCanvas>;
  viewId: ViewIds;
  isViewpointMode: boolean;
  labels: ExportLabels;
};

/**
 * Saves the canvas a view renders itself as a PNG, compositing it onto the export
 * background with the legend beside it. A missing container or canvas is a
 * failure the user hears about, not a silent no-op: with an asynchronous canvas
 * source it means the view had nothing to render.
 */
const pngExporter =
  ({
    getExportContainer,
    getExportCanvas,
    viewId,
    isViewpointMode,
    labels,
  }: PngExporterArgs) =>
  () =>
    runViewExport({
      viewId,
      format: EXPORT_FORMAT.PNG,
      isViewpointMode,
      labels,
      run: async ({ transaction, mutableTrackingData }) => {
        const container = getExportContainer();
        if (!container) {
          throw Error('export container not found');
        }

        const canvas = await getExportCanvas();
        if (!canvas) {
          throw Error('export canvas not found');
        }

        const viewLegend = container.querySelector(
          ViewLegendContainerElement.toString()
        ) as HTMLElement | null;

        const exportCanvas = await getCanvasWithBackgroundColor({
          profilingTransaction: transaction,
          mutableTrackingData,
          inputCanvas: canvas,
          color: colors.white950,
          getStyleSheets,
          viewLegend,
        });

        deliverExport(await canvasToBlob(exportCanvas), `${viewId}.png`);
      },
    });

export { pngExporter };
